import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import EmptyState from "@/components/EmptyState";
import { formatShortDate, formatPrice } from "@/lib/format";
import { CalendarPlus, Calendar, MapPin } from "lucide-react";

const STATUS_LABEL = {
  pending: { label: "En attente de validation", className: "z-badge" },
  pending_payment: { label: "Forfait à régler", className: "z-badge", style: { background: "rgba(218,113,1,0.12)", color: "var(--z-orange)" } },
  rejected: { label: "Rejeté", className: "z-badge", style: { background: "rgba(161,44,123,0.12)", color: "var(--z-error)" } },
  published: { label: "Publié", className: "z-badge z-badge-success" },
};

export default function OrgEventsPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/organizer/events").then((r) => setEvents(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="z-container" style={{ textAlign: "center" }}><span className="z-loader z-loader-dark" /></div>;

  return (
    <div className="z-container" data-testid="org-events-page">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18, gap: 12, flexWrap: "wrap" }}>
        <div>
          <h1 className="z-page-title">Mes événements</h1>
          <p className="z-page-sub" style={{ marginBottom: 0 }}>{events.length} événement(s)</p>
        </div>
        <Link to="/org/create" className="z-btn z-btn-primary" data-testid="org-create-event-btn"><CalendarPlus size={16} /> Nouveau</Link>
      </div>

      {events.length === 0 ? (
        <EmptyState
          icon={<CalendarPlus size={28} />}
          title="Aucun événement"
          message="Créez votre premier événement pour commencer."
          testId="org-events-empty"
        >
          <Link to="/org/create" className="z-btn z-btn-primary">Créer un événement</Link>
        </EmptyState>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 18 }}>
          {events.map((e) => {
            const status = STATUS_LABEL[e.status] || STATUS_LABEL.published;
            return (
              <Link key={e.id} to={`/org/event/${e.id}`} className="z-event-card" data-testid={`org-event-card-${e.id}`}>
                {e.image_url ? (
                  <img src={e.image_url} alt="" className="z-event-card-img" />
                ) : (
                  <div className="z-event-card-img" style={{ background: "linear-gradient(135deg,#01696F,#20808D)" }} />
                )}
                <div className="z-event-card-body">
                  <div style={{ marginBottom: 6 }}>
                    <span className={status.className} style={status.style || {}}>{status.label}</span>
                  </div>
                  <h3 className="z-event-card-title">{e.title}</h3>
                  <p className="z-event-card-meta">
                    <Calendar size={13} style={{ display: "inline", verticalAlign: "middle", marginRight: 4 }} />
                    {formatShortDate(e.starts_at)}
                  </p>
                  <p className="z-event-card-meta">
                    <MapPin size={13} style={{ display: "inline", verticalAlign: "middle", marginRight: 4 }} />
                    {e.location || "Lieu communiqué"}
                  </p>
                  <span className="z-event-card-price">{formatPrice(e.price, e.currency)}</span>
                  {e.reject_reason && (
                    <p style={{ marginTop: 8, fontSize: 12, color: "var(--z-error)" }}>
                      Motif : {e.reject_reason}
                    </p>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
